import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Divider,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Alert,
} from '@mui/material';
import { Download, Visibility, Add, Close, FileDownload, Layers } from '@mui/icons-material';
import AdminAPI from '../service/AdminAPI';
import Events from '../service/Events';

export default class BulkDownloadSheetPopup extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      layout: 'vertical',
      separateSheets: false,
      showPreview: false,
      previewData: null,
      isLoading: false,
      localSelectedCustomers: Array.isArray(props.selectedCustomers) ? props.selectedCustomers : []
    };
  }

  componentDidUpdate(prevProps) {
    const newSelectedCustomers = Array.isArray(this.props.selectedCustomers) ? this.props.selectedCustomers : [];

    if (prevProps.selectedCustomers !== this.props.selectedCustomers) {
      this.setState({ localSelectedCustomers: newSelectedCustomers });
    }
    
    if (!prevProps.show && this.props.show) {
      this.setState({ localSelectedCustomers: newSelectedCustomers });
    }
  }

  handleLayoutChange = (event) => {
    this.setState({ layout: event.target.value }, () => {
      if (this.state.showPreview) {
        this.forceUpdate();
      }
    });
  };

  handleSeparateSheetsChange = (event) => {
    this.setState({ separateSheets: event.target.checked }, () => {
      if (this.state.showPreview) {
        this.forceUpdate();
      }
    });
  };

  handleCustomerToggle = (customer) => {
    this.setState((prevState) => {
      const currentSelection = Array.isArray(prevState.localSelectedCustomers) ? prevState.localSelectedCustomers : [];
      const isSelected = currentSelection.some(c => c.userID === customer.userID);
      let newSelection;
      if (isSelected) {
        newSelection = currentSelection.filter(c => c.userID !== customer.userID);
      } else {
        newSelection = [...currentSelection, customer];
      }
      
      // Notify parent component of selection change
      if (this.props.onSelectionChange) {
        this.props.onSelectionChange(newSelection);
      }
      
      return { localSelectedCustomers: newSelection };
    });
  };

  handleSelectAllCustomers = (allCustomers) => {
    this.setState({ localSelectedCustomers: allCustomers });
    
    // Notify parent component of selection change
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange(allCustomers);
    }
  };

  handleDeselectAllCustomers = () => {
    this.setState({ localSelectedCustomers: [] });
    
    // Notify parent component of selection change
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange([]);
    }
  };

  handleSelectConfirmedCustomers = () => {
    const { allCustomers } = this.props;
    const availableCustomers = allCustomers ? allCustomers.filter(c => !c.isPending) : [];
    
    // Filter customers who have at least one order
    const customersWithOrders = availableCustomers.filter(customer => 
      customer.orders && customer.orders.length > 0
    );
    
    this.setState({ localSelectedCustomers: customersWithOrders });
    
    // Notify parent component of selection change
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange(customersWithOrders);
    }
  };

  generatePreview = () => {
    const { products } = this.props;
    const { layout, localSelectedCustomers, separateSheets } = this.state;

    if (separateSheets) {
      return this.generateSeparateSheetsPreview(localSelectedCustomers, products, layout);
    }

    if (layout === 'vertical') {
      return this.generateVerticalPreview(localSelectedCustomers, products);
    } else {
      return this.generateHorizontalPreview(localSelectedCustomers, products);
    }
  };

  generateSeparateSheetsPreview = (customers, products, layout) => {
    const previewCustomers = customers.slice(0, 2); // Preview first 2 customers
    // Sort products by SortID (assuming products have SortID property)
    const sortedProducts = [...products].sort((a, b) => (a.SortID || 0) - (b.SortID || 0));

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Separate Sheets-Modus:</strong> Jeder Kunde erhält sein eigenes Sheet im .ods-Datei.
            {customers.length > 2 && ` Vorschau zeigt nur die ersten 2 von ${customers.length} Kunden.`}
          </Typography>
        </Alert>

        {previewCustomers.map((customer, idx) => (
          <Box key={customer.userID} sx={{ mb: 3 }}>
            <Paper elevation={2} sx={{ p: 2, border: '2px solid #2e7d32' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Layers sx={{ color: '#2e7d32' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                  Sheet {idx + 1}: {customer.firstName} {customer.lastName}
                </Typography>
              </Box>

              {layout === 'vertical' ? (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Produkt</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Menge</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Preis</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedProducts.slice(0, 10).map((product) => {
                        const order = customer.orders.find(o => o.productID === product.ID);
                        const hasOrder = order && order.Confirmed;
                        const price = hasOrder ? (order.isFixed ? order.Price * order.Amount : order.CustomPrice) : 0;
                        
                        return (
                          <TableRow key={product.ID}>
                            <TableCell>{product.Name}</TableCell>
                            <TableCell align="center" sx={{ color: hasOrder ? 'inherit' : '#999' }}>
                              {hasOrder ? `${order.Amount}x` : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: hasOrder ? 'inherit' : '#999' }}>
                              {hasOrder ? `${price.toFixed(2)}€` : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {sortedProducts.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="caption" color="textSecondary">
                              ... und {sortedProducts.length - 10} weitere Produkte
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                        {sortedProducts.slice(0, 8).map((product) => (
                          <TableCell key={product.ID} align="center" sx={{ fontWeight: 'bold', fontSize: 9 }}>
                            {product.Name}
                          </TableCell>
                        ))}
                        {sortedProducts.length > 8 && (
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>...</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Menge</TableCell>
                        {sortedProducts.slice(0, 8).map((product) => {
                          const order = customer.orders.find(o => o.productID === product.ID);
                          const hasOrder = order && order.Confirmed;
                          return (
                            <TableCell key={product.ID} align="center" sx={{ color: hasOrder ? 'inherit' : '#999' }}>
                              {hasOrder ? `${order.Amount}x` : '-'}
                            </TableCell>
                          );
                        })}
                        {sortedProducts.length > 8 && <TableCell align="center">...</TableCell>}
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Preis</TableCell>
                        {sortedProducts.slice(0, 8).map((product) => {
                          const order = customer.orders.find(o => o.productID === product.ID);
                          const hasOrder = order && order.Confirmed;
                          const price = hasOrder ? (order.isFixed ? order.Price * order.Amount : order.CustomPrice) : 0;
                          return (
                            <TableCell key={product.ID} align="center" sx={{ color: hasOrder ? 'inherit' : '#999' }}>
                              {hasOrder ? `${price.toFixed(2)}€` : '-'}
                            </TableCell>
                          );
                        })}
                        {sortedProducts.length > 8 && <TableCell align="center">...</TableCell>}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ mt: 2, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Gesamt: {customer.orders
                    .filter(o => o.Confirmed)
                    .reduce((sum, o) => sum + (o.isFixed ? o.Price * o.Amount : o.CustomPrice), 0)
                    .toFixed(2)}€
                </Typography>
              </Box>
            </Paper>
          </Box>
        ))}

        {customers.length > 2 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              + {customers.length - 2} weitere Sheet(s) werden erstellt
            </Typography>
          </Alert>
        )}
      </Box>
    );
  };

  generateVerticalPreview = (customers, products) => {
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Kunde</TableCell>
              {products.slice(0, 5).map((product) => (
                <TableCell key={product.ID} align="center" sx={{ fontWeight: 'bold', fontSize: 10 }}>
                  {product.Name}
                </TableCell>
              ))}
              {products.length > 5 && (
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  ... +{products.length - 5} mehr
                </TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.slice(0, 5).map((customer) => {
              const totalPrice = customer.orders.reduce((sum, order) => 
                sum + (order.isFixed ? order.Price * order.Amount : order.CustomPrice), 0
              );
              
              return (
                <TableRow key={customer.userID}>
                  <TableCell>{`${customer.firstName} ${customer.lastName}`}</TableCell>
                  {products.slice(0, 5).map((product) => {
                    const order = customer.orders.find(o => o.productID === product.ID);
                    return (
                      <TableCell key={product.ID} align="center">
                        {order ? `${order.Amount}x` : '-'}
                      </TableCell>
                    );
                  })}
                  {products.length > 5 && <TableCell align="center">...</TableCell>}
                  <TableCell align="center">{totalPrice.toFixed(2)}€</TableCell>
                </TableRow>
              );
            })}
            {customers.length > 5 && (
              <TableRow>
                <TableCell colSpan={products.length + 2} align="center">
                  <Typography variant="body2" color="textSecondary">
                    ... und {customers.length - 5} weitere Kunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  generateHorizontalPreview = (customers, products) => {
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Produkt</TableCell>
              {customers.slice(0, 5).map((customer) => (
                <TableCell key={customer.userID} align="center" sx={{ fontWeight: 'bold', fontSize: 10 }}>
                  {customer.firstName} {customer.lastName}
                </TableCell>
              ))}
              {customers.length > 5 && (
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  ... +{customers.length - 5} mehr
                </TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.slice(0, 10).map((product) => {
              let totalAmount = 0;
              
              return (
                <TableRow key={product.ID}>
                  <TableCell>{product.Name}</TableCell>
                  {customers.slice(0, 5).map((customer) => {
                    const order = customer.orders.find(o => o.productID === product.ID);
                    if (order) totalAmount += order.Amount;
                    return (
                      <TableCell key={customer.userID} align="center">
                        {order ? `${order.Amount}x` : '-'}
                      </TableCell>
                    );
                  })}
                  {customers.length > 5 && <TableCell align="center">...</TableCell>}
                  <TableCell align="center">{totalAmount}x</TableCell>
                </TableRow>
              );
            })}
            {products.length > 10 && (
              <TableRow>
                <TableCell colSpan={customers.length + 2} align="center">
                  <Typography variant="body2" color="textSecondary">
                    ... und {products.length - 10} weitere Produkte
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  handleDownload = () => {
    const { layout, localSelectedCustomers, separateSheets } = this.state;

    if (localSelectedCustomers.length === 0) {
      Events.onResult.emit('warning', 'Bitte wählen Sie mindestens einen Kunden aus.');
      return;
    }

    this.setState({ isLoading: true });

    const customerIDs = localSelectedCustomers.map(c => c.userID);

    AdminAPI.RequestBulkExcelSheet(customerIDs, layout, separateSheets)
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        const layoutSuffix = separateSheets ? `separate_${layout}` : layout;
        link.setAttribute(
          'download',
          `Customers_${layoutSuffix}_${new Date().toISOString().split('T')[0]}.ods`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        
        Events.onResult.emit('success', 'Excel-Datei erfolgreich heruntergeladen!');
        this.setState({ isLoading: false });
        this.props.onClose();
      })
      .catch((error) => {
        console.error(error);
        Events.onResult.emit('error', 'Fehler beim Herunterladen der Datei');
        this.setState({ isLoading: false });
      });
  };

  render() {
    const { show, onClose, allCustomers } = this.props;
    const { layout, showPreview, isLoading, separateSheets } = this.state;

    if (!show) return null;

    // Ensure localSelectedCustomers is always an array
    const localSelectedCustomers = Array.isArray(this.state.localSelectedCustomers) ? this.state.localSelectedCustomers : [];
    
    const availableCustomers = allCustomers ? allCustomers.filter(c => !c.isPending) : [];
    const unselectedCustomers = availableCustomers.filter(
      ac => !localSelectedCustomers.some(sc => sc.userID === ac.userID)
    );

    const totalOrders = localSelectedCustomers.reduce((sum, customer) => sum + customer.orders.length, 0);
    const totalPrice = localSelectedCustomers.reduce((sum, customer) => {
      return sum + customer.orders.reduce((orderSum, order) => 
        orderSum + (order.isFixed ? order.Price * order.Amount : order.CustomPrice), 0
      );
    }, 0);

    return (
      <Dialog 
        open={show} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: { xs: '80vh', sm: '70vh' },
            borderRadius: { xs: '12px', sm: '20px' },
            overflow: 'hidden',
            margin: { xs: '16px', sm: '32px' },
            maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' }
          }
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            zIndex: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
          }}
        >
          <Close />
        </IconButton>

        {/* Header with gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
            pt: { xs: 3, sm: 4 },
            pb: { xs: 2.5, sm: 3 },
            px: { xs: 2, sm: 3 },
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <FileDownload sx={{ fontSize: { xs: '1.8rem', sm: '2.5rem' }, display: { xs: 'none', sm: 'block' } }} />
          <DialogTitle sx={{ p: 0, flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
              Excel-Sheet für mehrere Kunden herunterladen
            </Typography>
          </DialogTitle>
        </Box>

        <DialogContent sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: { xs: 1.5, sm: 0 } }}>
              <Typography variant="subtitle1">
                Ausgewählte Kunden: {localSelectedCustomers.length}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                {unselectedCustomers.length > 0 && (
                  <Button
                    size="small"
                    fullWidth={window.innerWidth < 600}
                    onClick={() => this.handleSelectAllCustomers(availableCustomers)}
                  >
                    Alle auswählen
                  </Button>
                )}
                {availableCustomers.length > 0 && (
                  <Button
                    size="small"
                    fullWidth={window.innerWidth < 600}
                    color="success"
                    onClick={this.handleSelectConfirmedCustomers}
                  >
                    Mit Bestellungen
                  </Button>
                )}
                {localSelectedCustomers.length > 0 && (
                  <Button
                    size="small"
                    fullWidth={window.innerWidth < 600}
                    color="secondary"
                    onClick={this.handleDeselectAllCustomers}
                  >
                    Alle abwählen
                  </Button>
                )}
              </Box>
            </Box>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 200, overflowY: 'auto' }}>
              {localSelectedCustomers.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {localSelectedCustomers.map((customer) => (
                    <Chip
                      key={customer.userID}
                      label={`${customer.firstName} ${customer.lastName}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={() => this.handleCustomerToggle(customer)}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" align="center">
                  Keine Kunden ausgewählt
                </Typography>
              )}
            </Paper>

            {unselectedCustomers.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Weitere Kunden hinzufügen:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 150, overflowY: 'auto' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {unselectedCustomers.map((customer) => (
                      <Chip
                        key={customer.userID}
                        label={`${customer.firstName} ${customer.lastName}`}
                        size="small"
                        variant="outlined"
                        onClick={() => this.handleCustomerToggle(customer)}
                        icon={<Add />}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Paper>
              </>
            )}
            
            {localSelectedCustomers.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2">
                  <strong>Gesamtbestellungen:</strong> {totalOrders}
                </Typography>
                <Typography variant="body2">
                  <strong>Gesamtpreis:</strong> {totalPrice.toFixed(2)}€
                </Typography>
              </Paper>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Sheet-Option</FormLabel>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={separateSheets}
                  onChange={this.handleSeparateSheetsChange}
                  sx={{
                    color: '#2e7d32',
                    '&.Mui-checked': {
                      color: '#2e7d32',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Layers sx={{ fontSize: 18, color: '#2e7d32' }} />
                    <Typography variant="body1">Separate Sheets pro Kunde</Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Jeder Kunde erhält sein eigenes Sheet in der .ods-Datei
                  </Typography>
                </Box>
              }
            />
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">
              Layout-Option {separateSheets && '(pro Sheet)'}
            </FormLabel>
            <RadioGroup
              value={layout}
              onChange={this.handleLayoutChange}
            >
              <FormControlLabel
                value="vertical"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Vertikal (Standard)</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {separateSheets 
                        ? 'Produkte in Zeilen'
                        : 'Kunden in Zeilen, Produkte in Spalten'}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="horizontal"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Horizontal</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {separateSheets 
                        ? 'Produkte in Spalten'
                        : 'Produkte in Zeilen, Kunden in Spalten'}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          {localSelectedCustomers.length > 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => this.setState({ showPreview: !showPreview })}
                  fullWidth
                >
                  {showPreview ? 'Vorschau ausblenden' : 'Vorschau anzeigen'}
                </Button>
              </Box>

              {showPreview && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Vorschau ({separateSheets ? 'Separate Sheets' : (layout === 'vertical' ? 'Vertikal' : 'Horizontal')}):
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                    Dies ist eine vereinfachte Vorschau. Die vollständige Excel-Datei enthält alle Daten.
                  </Typography>
                  {this.generatePreview()}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <Divider />

        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button 
            onClick={onClose} 
            disabled={isLoading}
            variant="outlined"
            size="large"
            fullWidth
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#ddd',
              color: '#666',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              py: { xs: 1.2, sm: 1.5 },
              order: { xs: 2, sm: 1 },
              '&:hover': {
                borderColor: '#bbb',
                bgcolor: '#f5f5f5'
              }
            }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Download />}
            onClick={this.handleDownload}
            disabled={isLoading || localSelectedCustomers.length === 0}
            sx={{
              bgcolor: '#2e7d32',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              py: { xs: 1.2, sm: 1.5 },
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
              order: { xs: 1, sm: 2 },
              '&:hover': {
                bgcolor: '#1b5e20',
                boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease',
              '&.Mui-disabled': {
                bgcolor: '#9e9e9e',
                color: 'white'
              }
            }}
          >
            {isLoading ? 'Wird heruntergeladen...' : 'Herunterladen'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

